// Atlas Phase 6 — Live workspace pure helpers (spec §5.1 / §6 #4).
//
// Global live search across 55k channels with duplicate names needs DELIBERATE
// ranking (spec §6 #4 flags it as undefined + load-bearing). One pure helper,
// one test. Also: region-from-group-prefix (reliable, unlike the removed
// fuzzy language filter) and auto-collapse for very large groups.

import { scoreQuality, codecBitrateEfficiency } from '@/lib/player/qualityVerdict';

export interface RankableChannel {
    sourceId: number;
    id: string;
    name: string;
    groupTitle?: string;
}

export interface LiveSearchOpts<T> {
    /** Whether a channel is a favourite — favourites rank first. */
    isFavorite?: (ch: T) => boolean;
    /** Atlas R6 (#17) — observed working-quality in [0,1] (reliability-derived).
     *  Optional; when provided it ranks WITHIN a name-match tier, above the length
     *  tiebreak (favourites → exact match → working-quality, per spec §6 #4).
     *  Day-one callers pass 0 / nothing, so the order is unchanged until observed
     *  history accrues. */
    workingQuality?: (ch: T) => number;
}

function clamp01(n: number): number {
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0;
}

/** Lowercase + collapse whitespace for case/space-insensitive matching. */
function norm(s: string): string {
    return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Parsed search query — the ONE canonical channel-name matcher consumed by
 * the Live global search, the in-group search, Guide and Manage (post-4.10
 * rule #2: one resolver per transformation, in every layer).
 *
 * Semantics: text in double quotes is an EXACT PHRASE that must appear with
 * word boundaries — `"france 2"` matches `FR: FRANCE 2 HD` but NOT
 * `FRANCE 24` (field case: a france 2 search drowned in FRANCE 24 hits).
 * Unquoted text keeps the loose substring behaviour.
 */
export interface ParsedLiveSearch {
    /** Quoted phrases, normalized — each must word-boundary-match. */
    phrases: string[];
    /** Remaining unquoted text, normalized — plain substring match. */
    loose: string;
    /** Precompiled phrase regexes (per-channel matching is a 55k hot loop). */
    phraseRes: RegExp[];
}

export function parseLiveSearch(raw: string): ParsedLiveSearch {
    const phrases: string[] = [];
    const rest = String(raw ?? '').replace(/"([^"]*)"/g, (_m, p: string) => {
        const n = norm(p);
        if (n) phrases.push(n);
        return ' ';
    });
    // A stray/unclosed quote is treated as whitespace (loose fallback).
    const loose = norm(rest.replace(/"/g, ' '));
    return {
        phrases,
        loose,
        // `\b` only against word-character edges — `\b` next to a symbol
        // (e.g. a phrase like "(hd)") can never match.
        phraseRes: phrases.map((p) => {
            const lead = /^\w/.test(p) ? '\\b' : '';
            const trail = /\w$/.test(p) ? '\\b' : '';
            return new RegExp(`${lead}${escapeRegExp(p)}${trail}`);
        }),
    };
}

/** True when `name` satisfies every quoted phrase AND the loose remainder. */
export function matchesLiveSearch(name: string, parsed: ParsedLiveSearch): boolean {
    const n = norm(name || '');
    for (const re of parsed.phraseRes) {
        if (!re.test(n)) return false;
    }
    if (parsed.loose && !n.includes(parsed.loose)) return false;
    return true;
}

/**
 * Rank a flat global live search. Returns ONLY matching channels, ordered:
 *   1. favourites first (the user's own list is the strongest signal)
 *   2. exact name match > starts-with > word-boundary/substring (tiers)
 *   3. observed working-quality (Atlas R6 #17 — optional, [0,1]) — a STRICT
 *      comparator key within a tier, not additive points. The previous
 *      additive form (quality 0..90 + 1pt/char length bonus) let name LENGTH
 *      overturn real quality differences once rounding compressed them
 *      (field case: an 8 Mbps 1080p50 variant ranked below 5 Mbps ones
 *      because its name was three characters longer).
 *   4. shorter names first (a tighter match), then alphabetical (stable).
 */
export function rankLiveSearch<T extends RankableChannel>(channels: T[], term: string, opts: LiveSearchOpts<T> = {}): T[] {
    const parsed = parseLiveSearch(term);
    // Tier basis: the full query with quotes stripped (tier scores behave
    // identically to the pre-quoting behaviour for unquoted input).
    const q = norm([...parsed.phrases, parsed.loose].filter(Boolean).join(' '));
    if (!q) return [];
    const isFav = opts.isFavorite ?? (() => false);
    const scored: { ch: T; tier: number; wq: number; len: number; key: string }[] = [];
    for (const ch of channels) {
        const n = norm(ch.name || '');
        if (!matchesLiveSearch(n, parsed)) continue;
        let tier = 0;
        if (isFav(ch)) tier += 1000;
        if (n === q) tier += 500;
        else if (n.startsWith(q)) tier += 200;
        else if (new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(n)) tier += 100;
        // Provider claims are NEVER an input here.
        const wq = opts.workingQuality ? clamp01(opts.workingQuality(ch)) : 0;
        scored.push({ ch, tier, wq, len: n.length, key: n });
    }
    scored.sort(
        (a, b) => b.tier - a.tier || b.wq - a.wq || a.len - b.len || a.key.localeCompare(b.key),
    );
    return scored.map((s) => s.ch);
}

/** Minimal observation-signal shape consumed by the search ranking. */
export interface SearchRankSignal {
    reliability: { verdict: string };
    lastSeen: {
        height: number | null;
        bitrate: number | null;
        fps: number | null;
        videoCodec: string | null;
    } | null;
}

/**
 * Observed working-quality for the search ranking, in [0,1] — fed to
 * `rankLiveSearch`'s `workingQuality` so same-tier name matches order by
 * MEASURED quality (a "france 2" search ranks the 1080p50 variant above
 * 720p25). Doctrine order preserved: reliability first (a failing channel
 * gets NO quality boost, an unmeasured one no penalty), measured res ×
 * bitrate × fps × codec (the canonical `scoreQuality` weights) refine within.
 */
export function observedSearchQuality(signal: SearchRankSignal | null | undefined): number {
    if (!signal) return 0;
    const v = signal.reliability.verdict;
    if (v === 'bad') return 0; // failing — never boosted by stale good metrics
    const rel = v === 'good' ? 1 : v === 'mixed' ? 0.5 : 0;
    const ls = signal.lastSeen;
    const q = ls
        ? (scoreQuality({
              height: ls.height,
              bitrateBps: ls.bitrate,
              fps: ls.fps,
              videoCodec: ls.videoCodec,
          }).score ?? 0)
        : 0;
    // scoreQuality steps bucket nearby bitrates (8.0M ≈ 4.9M) — a small
    // CONTINUOUS bitrate term breaks those ties so equal-bucket variants
    // order by measured rate. Uses the codec-normalized EFFECTIVE rate
    // (5 Mbps HEVC ≈ 8 Mbps H.264). Capped at 20 Mbps; weight kept below
    // one step of the verdict score so it never overturns a real res/fps gap.
    const effBitrate =
        ls?.bitrate && ls.bitrate > 0 ? ls.bitrate * codecBitrateEfficiency(ls.videoCodec) : 0;
    const bitrateRefine = Math.min(effBitrate, 20_000_000) / 20_000_000;
    return rel * 0.6 + q * 0.36 + bitrateRefine * 0.04;
}

/**
 * Extract a region/origin token from a group-name PREFIX (reliable, spec §5.1).
 * `"DE | Sport HD"` → `"DE"`, `"UK Sports"` → `"UK"`, `"Sport"` → null.
 * Only short uppercase-ish leading tokens (2–4 chars) count as a region.
 */
export function extractRegionPrefix(groupTitle: string | null | undefined): string | null {
    if (typeof groupTitle !== 'string') return null;
    const m = groupTitle.match(/^\s*([A-Za-z]{2,4})\s*[|:\-–]\s+/);
    if (m && m[1]) return m[1].toUpperCase();
    const lead = groupTitle.match(/^\s*([A-Z]{2,4})\b/);
    return lead && lead[1] ? lead[1].toUpperCase() : null;
}

/**
 * THE canonical region-facet key for a channel — the SINGLE resolver behind
 * every user-facing region filter (Live sidebar facet §5.1 + Guide facet §5.2).
 * Region prefix from the group title, falling back to the channel name. Returns
 * `null` when no region is detectable (the channel falls out of any active
 * region filter). Keep all four facet sites (channelList `filteredChannels` +
 * `countryPrefixes`, EpgFilters options, Guide selection) pointed here so Live
 * and Guide always show the same chip set and filter identically — repo rule
 * "one canonical resolver across every layer." NOTE: this is distinct from
 * `extractCountryPrefix` in `@/lib/epgMatch` — a conservative name→region
 * heuristic used only by the EPG-matching path in `stores/epg.ts`, deliberately
 * NOT shared with the facet so widening the facet can't shift EPG matches.
 */
export function channelRegionKey(
    groupTitle: string | null | undefined,
    name?: string | null,
): string | null {
    return extractRegionPrefix(groupTitle) ?? extractRegionPrefix(name);
}

/** A group with more than `threshold` channels auto-collapses (spec §5.1 "large" hint). */
export function shouldAutoCollapse(channelCount: number, threshold = 20): boolean {
    return channelCount > threshold;
}
